import {
  calculateInvoiceFromLines,
  displayGrossCents,
  eurosToCents,
  formatCentsAsEuros,
  grossToNetCents,
  lineNetAfterDiscountCents,
} from '@/utils/money';
import {
  invoiceLineGroupFields,
  type DraftLineInput,
  type InvoiceLineItem,
  type InvoicePreviewLine,
} from '@/hooks/useInvoices';

export const INVOICE_LINE_TYPES: DraftLineInput['lineType'][] = [
  'labor',
  'part',
  'service',
  'surcharge',
];
export const INVOICE_LINE_UNITS = ['piece', 'hour', 'liter', 'kg', 'meter', 'set', 'pair', 'box'];

const UNGROUPED_SORT_ORDER = 1_000_000;

export interface InvoiceEditorLine {
  key: string;
  lineType: DraftLineInput['lineType'];
  description: string;
  quantity: string;
  unit: string;
  /** VAT-INCLUSIVE unit price as typed by the user (docs/VAT-BASIS.md § 1). */
  unitPriceEuros: string;
  discountPercent: string;
  taxable: boolean;
  taxPercent: string;
  groupName: string | null;
  groupSortOrder: number;
  groupNotes: string | null;
}

export function parseEditorDecimal(value: string): number {
  return Number(value.replace(',', '.')) || 0;
}

/** VAT rate that applies to this line; a non-taxable line is always 0. */
export function editorLineVatPercent(line: InvoiceEditorLine): number {
  return line.taxable ? parseEditorDecimal(line.taxPercent) : 0;
}

/**
 * Net unit price stored for this line. The user types gross, so the conversion
 * happens here — once, on the way out (docs/VAT-BASIS.md § 4).
 */
export function editorLineUnitNetCents(line: InvoiceEditorLine): number {
  const grossCents = eurosToCents(line.unitPriceEuros) ?? 0;
  return grossToNetCents(grossCents, editorLineVatPercent(line));
}

export function editorLineNetCents(line: InvoiceEditorLine): number {
  return lineNetAfterDiscountCents(
    editorLineUnitNetCents(line),
    parseEditorDecimal(line.quantity),
    parseEditorDecimal(line.discountPercent),
  );
}

/** Line total as shown to the user: VAT-inclusive (same basis as the invoice). */
export function editorLineGrossCents(line: InvoiceEditorLine): number {
  return calculateInvoiceFromLines([editorLineToCalcLine(line)]).lineGrossCents[0] ?? 0;
}

function editorLineToCalcLine(line: InvoiceEditorLine): {
  unitPriceCents: number;
  quantity: number;
  discountPercent: number;
  taxable: boolean;
  taxPercent: number;
} {
  return {
    unitPriceCents: editorLineUnitNetCents(line),
    quantity: parseEditorDecimal(line.quantity),
    discountPercent: parseEditorDecimal(line.discountPercent),
    taxable: line.taxable,
    taxPercent: editorLineVatPercent(line),
  };
}

export function editorLineToDraft(line: InvoiceEditorLine): DraftLineInput {
  return {
    lineType: line.lineType,
    description: line.description.trim(),
    quantity: parseEditorDecimal(line.quantity),
    unit: line.unit,
    unitPriceCents: editorLineUnitNetCents(line),
    discountPercent: parseEditorDecimal(line.discountPercent),
    lineTotalCents: editorLineNetCents(line),
    taxable: line.taxable,
    taxPercent: line.taxable ? parseEditorDecimal(line.taxPercent) : null,
    ...invoiceLineGroupFields(line),
  };
}

export function invoiceLineToEditor(
  li: Pick<
    InvoiceLineItem | InvoicePreviewLine,
    | 'lineType'
    | 'description'
    | 'quantity'
    | 'unit'
    | 'unitPriceCents'
    | 'discountPercent'
    | 'taxable'
    | 'taxPercent'
    | 'groupName'
    | 'groupSortOrder'
    | 'groupNotes'
  > & { id?: string },
  fallbackTaxPercent: number,
  key: string,
): InvoiceEditorLine {
  const lineType = INVOICE_LINE_TYPES.includes(li.lineType as DraftLineInput['lineType'])
    ? (li.lineType as DraftLineInput['lineType'])
    : 'service';
  return {
    key,
    lineType,
    description: li.description,
    quantity: String(li.quantity),
    unit: li.unit || 'piece',
    unitPriceEuros: formatCentsAsEuros(
      displayGrossCents(li.unitPriceCents, li.taxable, li.taxPercent, fallbackTaxPercent),
    ),
    discountPercent: String(li.discountPercent ?? 0),
    taxable: li.taxable,
    taxPercent: String(li.taxPercent ?? fallbackTaxPercent),
    groupName: li.groupName ?? null,
    groupSortOrder: li.groupSortOrder ?? UNGROUPED_SORT_ORDER,
    groupNotes: li.groupNotes ?? null,
  };
}

export function emptyEditorLine(fallbackTaxPercent: number, key: string): InvoiceEditorLine {
  return {
    key,
    lineType: 'service',
    description: '',
    quantity: '1',
    unit: 'piece',
    unitPriceEuros: '0.00',
    discountPercent: '0',
    taxable: true,
    taxPercent: String(fallbackTaxPercent),
    groupName: null,
    groupSortOrder: UNGROUPED_SORT_ORDER,
    groupNotes: null,
  };
}

/** One VAT-rate group of the editor's live totals. */
export interface EditorTaxGroup {
  taxPercent: number;
  netCents: number;
  taxCents: number;
  grossCents: number;
}

export interface EditorTotals {
  /** Σ per-line net, before the invoice-level discount. */
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  /** subtotal − discount + tax. This is the VAT-inclusive amount to pay. */
  totalCents: number;
  taxBreakdown: EditorTaxGroup[];
  /** Per-line VAT-inclusive cents; Σ equals `totalCents`. */
  lineGrossCents: number[];
}

/**
 * Live totals for the editor: taxable base, VAT per rate group and the
 * VAT-inclusive total.
 *
 * Delegates to the same `calculateInvoiceFromLines` basis as staff-api so the
 * preview reconciles with the stored invoice to the cent.
 */
export function summarizeEditorLines(
  lines: readonly InvoiceEditorLine[],
  invoiceDiscountCents = 0,
): EditorTotals {
  const result = calculateInvoiceFromLines(lines.map(editorLineToCalcLine), invoiceDiscountCents);
  return {
    subtotalCents: result.subtotalCents,
    discountCents: result.discountCents,
    taxCents: result.taxCents,
    totalCents: result.totalCents,
    taxBreakdown: result.taxBreakdown,
    lineGrossCents: result.lineGrossCents,
  };
}
